/* eslint-disable no-restricted-syntax */
import slugify from 'slugify';
import { ResizeObserver } from '@juggle/resize-observer';
import { breakpoints } from '../../style/base/index';
import { Device, DomainConfig, GlobalConfig, GlobalContent, HTMLElementContent } from './index.types';
import { Fund } from '../fund-api/types/funds-list.types';

// BARREL IMPORTS
export * from './index.types';

export const INSTITUTIONAL_CONSENT_QUERY_PARAM_KEY = 'institutional-consent';
export const INSTITUTIONAL_CONSENT_PERSIST_QUERY_PARAM_KEY = 'institutional-consent-persist';

// DOM SERVICE
export const DomService = {
  createElement(tagName: string, content: HTMLElementContent = '', props: Record<string, string> = {}): HTMLElement {
    const element = document.createElement(tagName);
    element.innerHTML = typeof content === 'string' ? content : content.toString();
    Object.keys(props).forEach((prop) => {
      element.setAttribute(prop, props[prop]);
    });
    return element;
  },

  createObserver(element: HTMLElement, callback: (_: ResizeObserverEntry[]) => void) {
    if (!(element instanceof Element)) {
      return;
    }
    const observer = new ResizeObserver((entries) => callback(entries));
    observer.observe(element);
  },

  getGlobalConfig(): GlobalConfig {
    const element = document.querySelector('meta[name="global-config"]');
    const globalConfig = element ? JSON.parse(element.getAttribute('content')) : {};
    return globalConfig;
  },

  getDomainConfig(): DomainConfig {
    const element = document.querySelector('meta[name="domain-config"]');
    const domainConfig = element ? JSON.parse(element.getAttribute('content')) : {};
    return domainConfig;
  },

  getApiConfig(): string {
    const element = document.querySelector('meta[name="api-config"]');
    return element?.getAttribute('content') || '';
  },

  getGlobalContent(): GlobalContent {
    const element = document.querySelector('meta[name="global-content"]');
    const globalContent = element ? JSON.parse(element.getAttribute('content')) : {};
    return globalContent;
  },

  /**
   * Mimics Element.closest() but transverses Shadow Dom
   * https://stackoverflow.com/questions/54520554/custom-element-getrootnode-closest-function-crossing-multiple-parent-shadowd
   */
  closestElement(selector: string, base: Element = this): Element {
    // eslint-disable-next-line no-underscore-dangle
    function __closestFrom(el: Element | Window | Document): Element {
      if (!el || el === document || el === window) return null;
      const found = (el as Element).closest(selector);
      return found || __closestFrom(((el as Element).getRootNode() as ShadowRoot).host);
    }
    return __closestFrom(base);
  },

  lockBody() {
    document.body.style.height = '100%';
    document.body.style.overflow = 'hidden';
  },

  unlockBody() {
    document.body.style.height = '';
    document.body.style.overflow = '';
  },

  getLocationHostname(): string {
    // eslint-disable-next-line compat/compat
    return window.location.hostname;
  },

  isTouchDevice() {
    return (
      'ontouchstart' in window ||
      // eslint-disable-next-line compat/compat
      navigator.maxTouchPoints > 0
    );
  },

  fireWhenAboveOrEqual(breakpoint: keyof typeof breakpoints, callback) {
    if (window.matchMedia) {
      const mq = window.matchMedia(`(min-width: ${breakpoints[breakpoint]}px)`);
      if (mq.matches) {
        callback();
      }
      if (mq.addEventListener) {
        mq.addEventListener('change', () => {
          if (mq.matches) {
            callback();
          }
        });
      }
    }
  },

  fireWhenBelow(breakpoint: keyof typeof breakpoints, callback) {
    if (window.matchMedia) {
      const mq = window.matchMedia(`(max-width: ${breakpoints[breakpoint] - 1}px)`);
      if (mq.matches) {
        callback();
      }
      if (mq.addEventListener) {
        mq.addEventListener('change', () => {
          if (mq.matches) {
            callback();
          }
        });
      }
    }
  },

  fireWhenBetween(breakpointMin: keyof typeof breakpoints, breakpointMax: keyof typeof breakpoints, callback) {
    if (window.matchMedia) {
      const mq = window.matchMedia(`(min-width: ${breakpoints[breakpointMin] - 1}px) and (max-width: ${breakpoints[breakpointMax] - 1}px)`);
      if (mq.matches) {
        callback();
      }
      if (mq.addEventListener) {
        mq.addEventListener('change', () => {
          if (mq.matches) {
            callback();
          }
        });
      }
    }
  },

  isSmallScreen() {
    return window.matchMedia && window.matchMedia(`(max-width: ${breakpoints.medium - 1}px)`).matches;
  },

  isBigScreen() {
    return window.matchMedia && window.matchMedia(`(min-width: ${breakpoints.xlarge}px)`).matches;
  },

  visitSite(url: string) {
    window.location.href = url;
  },

  setFundsHashtableToLocalStorage(channel, funds) {
    const map = new Map();

    const global = this.getGlobalConfig();

    for (const fund of funds) {
      fund.tranchen
        .sort((a, b) => a.sortierung - b.sortierung)
        .forEach((tranche) => {
          const nameToSlugify =
            tranche.ertragsTypZusatz !== 'R' && tranche.ertragsTypZusatz !== 'I' ? `${fund.fondsName} ${tranche.ertragsTypZusatz}` : fund.fondsName;
          const urlName = slugify(nameToSlugify);
          if (!map.has(urlName)) {
            map.set(urlName, {
              hash: fund.hash,
              tranche: tranche.hash,
              primaerTranche: tranche.primaertrancheHash,
            });
          }
        });
      localStorage.setItem(`${channel}-funds-${global.country}-${global.locale}`, JSON.stringify(Array.from(map.entries())));
    }
  },

  setFundsHashtableToLocalStorageV3(channel: string, funds: Fund[]) {
    const map = new Map();

    const global = this.getGlobalConfig();

    for (const fund of funds) {
      fund.tranches
        .sort((a, b) => a.sorting - b.sorting)
        .forEach((tranche) => {
          const nameToSlugify = tranche.typeAddition !== 'R' && tranche.typeAddition !== 'I' ? `${fund.name} ${tranche.typeAddition}` : fund.name;
          const urlName = slugify(nameToSlugify);
          if (!map.has(urlName)) {
            map.set(urlName, {
              hash: fund.hash,
              tranche: tranche.hash,
              primaerTranche: tranche.primaryTrancheHash,
            });
          }
        });
    }

    localStorage.setItem(`${channel}-funds-${global.country}-${global.locale}`, JSON.stringify(Array.from(map.entries())));
  },

  async getFundsHashtableFromLocalStorage(channel: string) {
    const global = this.getGlobalConfig();

    const funds = localStorage.getItem(`${channel}-funds-${global.country}-${global.locale}`);
    if (funds) {
      const map = new Map(JSON.parse(funds));
      const [[, values]] = map.entries();

      // Fallback for missing localstorage data
      if (!Object.prototype.hasOwnProperty.call(values, 'primaerTranche')) {
        return null;
      }

      return map;
    }

    return null;
  },

  getDeviceType(): Device {
    let device = Device.LAPTOP;

    if (window.innerWidth < breakpoints.large) {
      device = Device.TABLET;
    }
    if (window.innerWidth < breakpoints.medium) {
      device = Device.MOBILE;
    }

    return device;
  },

  getImageForDeviceType(
    device: Device,
    { imageSrcDesktop, imageSrcTablet, imageSrcMobile }: { imageSrcDesktop: string; imageSrcTablet: string; imageSrcMobile: string },
  ) {
    let imageSrc = imageSrcDesktop;

    if (imageSrcTablet && (device === Device.TABLET || device === Device.MOBILE)) {
      imageSrc = imageSrcTablet;
    }
    if (imageSrcMobile && device === Device.MOBILE) {
      imageSrc = imageSrcMobile;
    }

    return imageSrc;
  },

  getBackgroundOrNull(src: string) {
    return typeof src === 'string'
      ? {
          backgroundImage: `url('${src}')`,
        }
      : null;
  },

  isIOS() {
    const iosPlatformRegex = new RegExp(/iPad|iPhone|iPod'?/gi);

    return (
      iosPlatformRegex.test(navigator.userAgent) ||
      // iPad on iOS 13 and up shows Mac as user agent, therefore additional check is needed for iPads
      (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
    );
  },

  isArticle() {
    return !!document.querySelector('rcm-layout-article');
  },

  getQueryParam(key: string) {
    const params = new URLSearchParams(window.location.search);
    return params.get(key);
  },

  appendQueryParam(url: string, param: { key: string; value: string }): string {
    const parsedUrl = new URL(url, window.location.origin);
    parsedUrl.searchParams.set(param.key, param.value);
    return parsedUrl.toString();
  },

  removeQueryParam(url: string, key: string): string {
    const parsedUrl = new URL(url, window.location.origin);
    parsedUrl.searchParams.delete(key);
    return parsedUrl.toString();
  },
};
